import React, { useEffect, useState } from "react";
import { Card, Row, Col, Typography, Progress, Spin, message } from "antd";
import {
  EyeOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { getDashboardData } from "../../services/Dashboard";
import { useAuth } from "../../contexts/AuthContext";

const { Title, Text } = Typography;

const UserDashboard = () => {
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  console.log(user);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getDashboardData(user._id);
        setDashboardData(data.analytics);
      } catch (error) {
        message.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user._id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  const stats = [
    {
      title: "Total Eye Analyses",
      value: dashboardData?.totalEyeAnalysis || 0,
      icon: <EyeOutlined />,
      subtext: "Scans completed",
    },
    {
      title: "Last Analysis Date",
      value: dashboardData?.lastAnalysisDate
        ? new Date(dashboardData.lastAnalysisDate).toLocaleDateString()
        : "—",
      icon: <ClockCircleOutlined />,
      subtext: "Most recent scan",
    },
    {
      title: "Saved Reports",
      value: dashboardData?.savedReportCount || 0,
      icon: <FileTextOutlined />,
      subtext: "Generated insights",
    },
  ];

  const recentReports = dashboardData?.recentReports || [];

  return (
    <div className="min-h-screen bg-white p-8">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Title level={3} className="!text-[#1c5a85] !font-semibold">
            Eye Health Dashboard
          </Title>
          <Text className="text-gray-500 text-sm">
            A summary of your recent analyses and health insights.
          </Text>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[24, 24]}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card
              bordered={false}
              className="rounded-2xl shadow-[0_4px_16px_rgba(28,90,133,0.08)] hover:shadow-[0_6px_20px_rgba(28,90,133,0.12)] transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div>
                  <Text className="text-gray-500 text-xs uppercase tracking-wide">
                    {stat.title}
                  </Text>
                  <Title
                    level={2}
                    className="!m-0 !text-[#1c5a85] font-semibold"
                  >
                    {stat.value}
                  </Title>
                  <Text className="text-gray-400 text-xs">{stat.subtext}</Text>
                </div>
                <div className="bg-[#1c5a85]/10 text-[#1c5a85] p-3 rounded-full text-2xl">
                  {stat.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Reports Section */}
      <div className="mt-10">
        <Card
          bordered={false}
          className="rounded-2xl shadow-[0_4px_16px_rgba(28,90,133,0.08)]"
        >
          <Title level={4} className="!text-[#1c5a85] !mb-4">
            Recent Reports
          </Title>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recentReports.length > 0 ? (
              recentReports.map((report, i) => (
                <div
                  key={report._id || i}
                  className="border border-[#e8f1f7] rounded-xl p-4 hover:bg-[#f9fbfc] transition-all"
                >
                  <Text className="block text-sm text-gray-600 font-medium">
                    Report #{i + 1}
                  </Text>
                  <Text className="block text-xs text-gray-400">
                    Conducted on {new Date(report.date).toLocaleDateString()}
                  </Text>
                  <div className="mt-2 text-[#1c5a85] font-medium text-sm">
                    {report.summary || "No summary provided"}
                  </div>
                </div>
              ))
            ) : (
              <Text className="text-gray-400 text-sm">No reports found.</Text>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UserDashboard;
